import type { VoidFunctionComponent } from 'react';
import Locator from '../../../../../../shared/Locator';
import styles from './DependencyRow.module.less';
import ListRowDetailsSeparator from '../../../../generic/List/ListRowDetailsSeparator';
import FetcherIcon from '../../../../generic/FetcherIcon';
import Tooltip from '../../../../generic/Tooltip';
import type { DependencyV2Modals } from '../../hooks/useDependencyModals';
import Button, { BUTTON_TYPE } from '../../../../generic/Button';
import type { Dependency } from '../../../../../../shared/dependenciesV2';
import { CopiableText } from '../../../../generic';
import type { Fetcher } from '../../../../../../shared/fetchers';
import { fetcherToPlainText } from '../../../../../../shared/fetchers';

type Props = {
  dependency: Dependency;
  modals: DependencyV2Modals;
  isProjectScope: boolean;
};

const DependencyData: VoidFunctionComponent<Props> = ({ dependency, modals, isProjectScope }) => {
  const revision = new Locator(dependency.locator);
  const fetcher = revision.fetcher.full || '';
  const fetcherName = fetcherToPlainText(fetcher as Fetcher) ?? 'Unknown';
  const locator = revision.toFullString();
  const originPathsText = dependency.originPaths.join(', ');
  const isTransitive = dependency.depth > 1;
  // Release groups might have multiple CPEs for the same dependency, but they are most likely the same.
  // For now, we just show the first one.
  const cpe = dependency.cpes[0];

  const openPathModal = () =>
    modals.viewRelationshipsModal.onOpen({
      context: { title: dependency.title, revision: locator },
    });

  return (
    <div className={styles.DependencyInfoData}>
      <div className={styles.fetcher}>
        <Tooltip id={`${dependency.locator}-fetcher`} tooltip={fetcherName}>
          {/* Note that this span is required to enable the tooltip on an SVG. */}
          <span>
            <FetcherIcon fetcher={fetcher} />
          </span>
        </Tooltip>
      </div>
      <ListRowDetailsSeparator />
      <CopiableText id={locator} name="locator" value={locator} />
      {dependency.originPaths && dependency.originPaths.length > 0 && (
        <>
          <ListRowDetailsSeparator />
          <Tooltip id={`${locator}-originpaths`} tooltip={`Origin: ${originPathsText}`}>
            <span className={styles.originPaths}>{originPathsText}</span>
          </Tooltip>
        </>
      )}
      {cpe && (
        <>
          <ListRowDetailsSeparator />
          <CopiableText id={locator} name="CPE" value={cpe} />
        </>
      )}
      {isTransitive && isProjectScope && (
        <>
          <ListRowDetailsSeparator />
          <Button type={BUTTON_TYPE.ANCHOR} onClick={openPathModal}>
            View path
          </Button>
        </>
      )}
    </div>
  );
};

export default DependencyData;
